import React, { useState, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { toast, ToastContainer } from 'react-toastify';
import { FiClock, FiCalendar } from 'react-icons/fi';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const BookingModal = ({
  formData,
  setFormData,
  selectedService,
  onClose,
  confirmServiceBooking,
}) => {
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingId, setBookingId] = useState('');

  // Get booking ID from sessionStorage or localStorage when component mounts
  useEffect(() => {
    const getBookingId = () => {
      // First check sessionStorage
      let bookingDetails = JSON.parse(sessionStorage.getItem('bookingDetails')) || {};
      let id = bookingDetails.primaryBooking?.booking_id || '';

      // If not found in sessionStorage, check localStorage
      if (!id) {
        bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};
        id = bookingDetails.primaryBooking?.booking_id || '';
        console.log('BookingModal: Booking ID retrieved from localStorage:', id);
      } else {
        console.log('BookingModal: Booking ID retrieved from sessionStorage:', id);
      }

      setBookingId(id);
    };

    getBookingId();
  }, []);

  // Fetch all items once when component mounts
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/`);
        const data = await response.json();
        setAllItems(data);
      } catch (error) {
        console.error('Error fetching all items:', error);
      }
    };
    fetchAllItems();
  }, []);

  // Fetch Subcategories (Services under the selected category)
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-services/`);
        const data = await response.json();

        // Filter services under the selected category
        const filteredSubcategories = data.filter((sub) => sub.categoryid === selectedService?.id);

        setSubcategories(filteredSubcategories);
        // Reset serviceid when category changes
        setFormData((prev) => ({ ...prev, serviceid: '' }));
      } catch (error) {
        console.error('Error fetching subcategories:', error);
      }
    };

    if (selectedService) {
      fetchSubcategories();
    }
  }, [selectedService, setFormData]);

  // Filter items when service is selected or when category changes
  useEffect(() => {
    if (formData.serviceid) {
      // Filter items based on service ID
      const filteredItems = allItems.filter((item) => item.serviceid == formData.serviceid);
      setItems(filteredItems);
    } else if (selectedService?.id) {
      // If no service selected but category is selected, show items for that category
      const filteredItems = allItems.filter((item) => item.categoryid === selectedService.id);
      setItems(filteredItems);
    }
  }, [formData.serviceid, allItems, selectedService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Calculate tax based on value and value_type
  const calculateTax = (amount, value, valueType) => {
    if (!value || value === 0) return 0;

    if (valueType === 'P') {
      // Percentage
      return (amount * value) / 100;
    } else if (valueType === 'F') {
      // Fixed amount
      return value;
    }
    return 0;
  };

  // Get tax rate for item or service
  const getTaxRate = () => {
    if (formData.itemid) {
      const selectedItem = items.find((item) => item.id == formData.itemid);
      return {
        value: selectedItem?.value || selectedItem?.tax_value || 0,
        valueType: selectedItem?.value_type || selectedItem?.tax_value_type || 'P',
      };
    } else if (formData.serviceid) {
      const selectedServiceData = subcategories.find((sub) => sub.id == formData.serviceid);
      return {
        value: selectedServiceData?.value || selectedServiceData?.tax_value || 0,
        valueType: selectedServiceData?.value_type || selectedServiceData?.tax_value_type || 'P',
      };
    }
    return { value: 0, valueType: 'P' };
  };

  // Calculate full breakdown including tax
  const calculateFullBreakdown = (subtotal) => {
    const taxRate = getTaxRate();
    const taxAmount = calculateTax(subtotal, taxRate.value, taxRate.valueType);
    const total = subtotal + taxAmount;
    const taxPercentage = taxRate.valueType === 'P' ? taxRate.value : 0;

    return {
      subtotal,
      taxAmount,
      total,
      taxRate: taxRate.value,
      taxType: taxRate.valueType,
      taxPercentage: taxPercentage,
      cgst: taxRate.valueType === 'P' ? taxAmount / 2 : 0,
      sgst: taxRate.valueType === 'P' ? taxAmount / 2 : 0,
    };
  };

  // UPDATED: Using the same pattern as AmenityBooking
  const handleDateTimeChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      // Initialize with current date/time if not set
      const currentDate = prev.scheduledDate || new Date().toISOString().split('T')[0];
      const currentTime = prev.scheduledTime || new Date().toTimeString().slice(0, 5);

      let newDate = currentDate;
      let newTime = currentTime;

      if (name === 'scheduledDate') {
        newDate = value;
      } else if (name === 'scheduledTime') {
        newTime = value;
      }

      // Create the combined datetime string for API
      const combinedDateTime = newDate && newTime ? `${newDate}T${newTime}:00` : null;

      return {
        ...prev,
        scheduledDate: newDate,
        scheduledTime: newTime,
        scheduledtime: combinedDateTime, // For API
      };
    });
  };

  const handlePreview = async () => {
    // If no item selected, we'll use the default service pricing
    if (!formData.serviceid) {
      alert('Please select a service first');
      return;
    }

    // Validate date and time
    if (!formData.scheduledDate || !formData.scheduledTime) {
      alert('Please select both date and time');
      return;
    }

    try {
      let previewResponse;
      let selectedItem = null;
      let taxRate = { value: 0, valueType: 'P' };

      if (formData.itemid && formData.quantity) {
        // If item is selected, get preview for that specific item
        selectedItem = items.find((item) => item.id == formData.itemid);
        if (!selectedItem) {
          throw new Error('Selected item not found');
        }

        previewResponse = await fetch(
          `${BQ_BASE_URL}/bq/api/room-service-requests/preview/${formData.itemid}/${formData.quantity}`
        );

        // Get tax rate from selected item
        taxRate = {
          value: selectedItem.value || selectedItem.tax_value || 0,
          valueType: selectedItem.value_type || selectedItem.tax_value_type || 'P',
        };
      } else {
        // If no item selected, get preview for the default service
        previewResponse = await fetch(
          `${BQ_BASE_URL}/bq/api/room-service-requests/preview-service/${formData.serviceid}`
        );

        // Get tax rate from selected service
        const selectedServiceData = subcategories.find((sub) => sub.id == formData.serviceid);
        taxRate = {
          value: selectedServiceData?.value || selectedServiceData?.tax_value || 0,
          valueType: selectedServiceData?.value_type || selectedServiceData?.tax_value_type || 'P',
        };
      }

      const data = await previewResponse.json();

      setPreviewData({
        item_name: data.item_name || selectedService?.name || 'Service',
        base_amount: data.base_amount || 0,
        urgent_amount: data.urgent_amount || 0,
        quantity: data.quantity || formData.quantity || 1,
        tax_value: taxRate.value,
        tax_value_type: taxRate.valueType,
      });

      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching preview:', error);

      // Fallback to showing just the service name if API fails
      const taxRate = getTaxRate();
      setPreviewData({
        item_name: selectedService?.name || 'Service',
        base_amount: 0,
        urgent_amount: 0,
        quantity: 1,
        tax_value: taxRate.value,
        tax_value_type: taxRate.valueType,
      });
      setShowPreview(true);
    }
  };

  const onBook = async (formData) => {
    const apiUrl = `${BQ_BASE_URL}/bq/api/room-service-requests/`;

    // Validate date and time
    if (!formData.scheduledDate || !formData.scheduledTime) {
      toast.error('Please select both date and time');
      return;
    }

    try {
      if (!bookingId) {
        throw new Error('Booking ID not found. Please try again.');
      }

      // Validate required fields - only service is required now
      if (!formData.serviceid) {
        throw new Error('Please select a service');
      }

      // Calculate amount based on whether item is selected or not
      let baseAmount = 0;
      let urgentAmount = 0;
      let selectedItem = null;
      let taxRate = { value: 0, valueType: 'P' };

      if (formData.itemid && formData.quantity) {
        selectedItem = items.find((item) => item.id == formData.itemid);
        if (!selectedItem) {
          throw new Error('Selected item not found');
        }
        baseAmount = selectedItem.baseprice * formData.quantity;
        urgentAmount = selectedItem.urgentprice * formData.quantity;
        taxRate = {
          value: selectedItem.value || selectedItem.tax_value || 0,
          valueType: selectedItem.value_type || selectedItem.tax_value_type || 'P',
        };
      } else {
        // Get tax rate from selected service
        const selectedServiceData = subcategories.find((sub) => sub.id == formData.serviceid);
        taxRate = {
          value: selectedServiceData?.value || selectedServiceData?.tax_value || 0,
          valueType: selectedServiceData?.value_type || selectedServiceData?.tax_value_type || 'P',
        };
      }

      const subtotal = formData.urgencylevel === 'Urgent' ? urgentAmount : baseAmount;
      const taxAmount = calculateTax(subtotal, taxRate.value, taxRate.valueType);
      const totalAmount = subtotal + taxAmount;

      const requestBody = {
        bookingid: bookingId,
        categoryid: selectedService?.id,
        serviceid: formData.serviceid,
        itemid: formData.itemid || null,
        quantity: formData.quantity || 1,
        urgencylevel: formData.urgencylevel || 'Normal',
        scheduledtime: formData.scheduledtime,
        specialinstructions: formData.specialinstructions || 'No instructions',
        status: 'Pending',
        base_amount: subtotal,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        tax_value: taxRate.value,
        tax_value_type: taxRate.valueType,
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      toast.success('Room service booked successfully!');

      await confirmServiceBooking();
      onClose();
      return data;
    } catch (error) {
      console.error('Error making booking:', error);
      toast.error(`Booking failed: ${error.message}`);
      throw error;
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date for display
  const formatDisplayDateTime = () => {
    if (formData.scheduledDate && formData.scheduledTime) {
      const date = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return 'Not scheduled';
  };

  // Get tax display text
  const getTaxDisplayText = (value, valueType) => {
    if (!value || value === 0) return 'Tax not applicable';
    if (valueType === 'P') return `${value}% GST`;
    if (valueType === 'F') return `₹${value} fixed tax`;
    return 'Including taxes';
  };

  // Get the current tax rate for display
  const currentTaxRate = getTaxRate();

  return (
    <>
      {/* Main Booking Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
          <div className="p-6 relative">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-poppins font-bold text-gray-800">
                  Book {selectedService?.name}
                </h2>
                <p className="text-gray-500 font-poppins text-sm mt-1">
                  Fill in the details to schedule your service
                </p>
              </div>
              <button
                className="text-red-500 hover:text-red-700 transition-colors duration-200"
                onClick={onClose}
              >
                <IoClose size={32} />
              </button>
            </div>

            <input type="hidden" name="categoryid" value={selectedService?.id} />
            <input type="hidden" name="status" value="Pending" />

            <div className="grid grid-cols-2 gap-6">
              {/* Service and Item in one row */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Select a Service <span className="text-red-500">*</span>
                </label>
                <select
                  name="serviceid"
                  value={formData.serviceid}
                  onChange={handleInputChange}
                  className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
                  required
                >
                  <option value="" disabled>
                    Select a service
                  </option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Items Dropdown - now optional */}
              {items.length > 0 && (
                <div className="col-span-1">
                  <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                    Select an Item
                  </label>
                  <select
                    name="itemid"
                    value={formData.itemid || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
                  >
                    <option value="">Select your specific item</option>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Urgency Level and Quantity in one row */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Urgency Level
                </label>
                <select
                  name="urgencylevel"
                  value={formData.urgencylevel || 'Normal'}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
                >
                  <option value="Normal">Normal</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              {/* Quantity - only shown if item is selected */}
              {formData.itemid && (
                <div className="col-span-1">
                  <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    placeholder="Enter Your Quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
                  />
                </div>
              )}

              {/* Schedule Date field */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Schedule Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate || ''}
                  onChange={handleDateTimeChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  required
                />
              </div>

              {/* Schedule Time field */}
              <div className="col-span-1">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Schedule Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="scheduledTime"
                  value={formData.scheduledTime || ''}
                  onChange={handleDateTimeChange}
                  className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  required
                />
              </div>

              {/* Special Instructions - full width */}
              <div className="col-span-2">
                <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
                  Special Instructions
                </label>
                <textarea
                  name="specialinstructions"
                  value={formData.specialinstructions}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
                  placeholder="Any special requests or instructions"
                ></textarea>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={handlePreview}
                className="px-10 py-2.5 font-poppins bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Preview
              </button>
              <button
                onClick={async () => {
                  setIsBooking(true);
                  try {
                    await onBook(formData);
                  } catch (error) {
                    // Error is already handled by the toast in onBook
                  } finally {
                    setIsBooking(false);
                  }
                }}
                disabled={isBooking}
                className={`px-6 py-2.5 font-poppins text-white font-medium rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
                  isBooking ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isBooking ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Book Now'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scaleIn">
            <div className="relative">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-black to-gray-700 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Order Summary</h2>
                    <p className="text-blue-100 text-sm mt-1">
                      Review your order before confirmation
                    </p>
                  </div>
                  <button
                    className="text-white hover:text-blue-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10"
                    onClick={closePreview}
                  >
                    <IoClose size={24} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">
                {/* Order Items Card */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{previewData?.item_name}</h3>
                      {formData.itemid && (
                        <p className="text-sm text-gray-500">Qty: {previewData?.quantity}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Urgency:</span>
                      <span
                        className={`font-medium ${
                          formData.urgencylevel === 'Urgent' ? 'text-red-500' : 'text-gray-700'
                        }`}
                      >
                        {formData.urgencylevel || 'Normal'}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">Scheduled for:</span>
                      <div className="text-right">
                        <div className="flex items-center text-gray-700 font-medium">
                          <FiCalendar className="mr-1 text-gray-400" size={14} />
                          {formatDisplayDateTime()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <h4 className="font-medium text-lg text-black mb-4">Price Details</h4>
                  <div className="space-y-3">
                    {/* Subtotal */}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">
                        ₹
                        {formatCurrency(
                          formData.urgencylevel === 'Urgent'
                            ? previewData?.urgent_amount
                            : previewData?.base_amount
                        )}
                      </span>
                    </div>

                    {/* Taxes and Charges - Dynamic based on API */}
                    {previewData?.tax_value > 0 && (
                      <div className="border-gray-600">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600 flex items-center">
                            Taxes and Charges
                            <div className="group relative ml-1">
                              <svg
                                className="w-4 h-4 text-gray-400 cursor-pointer"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-white text-black text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10 shadow-lg">
                                <div className="font-semibold mb-2 text-sm">Tax Breakdown:</div>
                                <div className="flex flex-col space-y-1">
                                  {previewData.tax_value_type === 'P' ? (
                                    <>
                                      <div>
                                        {getTaxDisplayText(
                                          previewData.tax_value,
                                          previewData.tax_value_type
                                        )}
                                      </div>
                                      <div>
                                        CGST: ₹
                                        {formatCurrency(
                                          ((formData.urgencylevel === 'Urgent'
                                            ? previewData?.urgent_amount
                                            : previewData?.base_amount) *
                                            (previewData.tax_value / 100)) /
                                            2
                                        )}
                                      </div>
                                      <div>
                                        SGST: ₹
                                        {formatCurrency(
                                          ((formData.urgencylevel === 'Urgent'
                                            ? previewData?.urgent_amount
                                            : previewData?.base_amount) *
                                            (previewData.tax_value / 100)) /
                                            2
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <div>
                                      {getTaxDisplayText(
                                        previewData.tax_value,
                                        previewData.tax_value_type
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="absolute top-full left-3 -mt-1 border-4 border-transparent border-t-gray-800"></div>
                              </div>
                            </div>
                          </span>
                          <span className="font-medium">
                            ₹
                            {formatCurrency(
                              (formData.urgencylevel === 'Urgent'
                                ? previewData?.urgent_amount
                                : previewData?.base_amount) *
                                (previewData.tax_value / 100) || previewData.tax_value
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className="pt-3 border-t border-gray-200 flex justify-between">
                      <span className="font-semibold text-gray-800">Total</span>
                      <span className="font-bold text-blue-600">
                        ₹
                        {formatCurrency(
                          calculateFullBreakdown(
                            formData.urgencylevel === 'Urgent'
                              ? previewData?.urgent_amount
                              : previewData?.base_amount
                          ).total
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Special Instructions */}
                {formData.specialinstructions && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <h4 className="font-medium text-gray-700 mb-2">Special Instructions</h4>
                    <p className="text-gray-700 italic">"{formData.specialinstructions}"</p>
                  </div>
                )}
              </div>

              {/* Footer with confirm button only */}
              <div className="px-6 pb-6 pt-2 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={async () => {
                    setIsBooking(true);
                    try {
                      await onBook(formData);
                    } catch (error) {
                      // Error is already handled
                    } finally {
                      setIsBooking(false);
                    }
                  }}
                  disabled={isBooking}
                  className={`w-full py-3 px-4 bg-black rounded-lg font-semibold text-white hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm ${
                    isBooking ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isBooking ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default BookingModal;
// import React, { useState, useEffect } from "react";
// import { IoClose } from "react-icons/io5";
// import { toast, ToastContainer } from "react-toastify";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const BookingModal = ({
//   formData,
//   setFormData,
//   selectedService,
//   onClose,
//   confirmServiceBooking,
// }) => {
//   const [subcategories, setSubcategories] = useState([]);
//   const [items, setItems] = useState([]);
//   const [allItems, setAllItems] = useState([]);
//   const [previewData, setPreviewData] = useState(null);
//   const [showPreview, setShowPreview] = useState(false);
//   const [isBooking, setIsBooking] = useState(false);
//   const [bookingId, setBookingId] = useState("");

//   // Get booking ID from session storage when component mounts
//   useEffect(() => {
//     const bookingDetails =
//       JSON.parse(sessionStorage.getItem("bookingDetails")) || {};
//     const primaryBookingId = bookingDetails.primaryBooking?.booking_id || "";
//     setBookingId(primaryBookingId);
//   }, []);

//   // Fetch all items once when component mounts
//   useEffect(() => {
//     const fetchAllItems = async () => {
//       try {
//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/room-service-items/`
//         );
//         const data = await response.json();
//         setAllItems(data);
//       } catch (error) {
//         console.error("Error fetching all items:", error);
//       }
//     };
//     fetchAllItems();
//   }, []);

//   // Fetch Subcategories (Services under the selected category)
//   useEffect(() => {
//     const fetchSubcategories = async () => {
//       try {
//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/room-service-services/`
//         );
//         const data = await response.json();

//         // Filter services under the selected category
//         const filteredSubcategories = data.filter(
//           (sub) => sub.categoryid === selectedService?.id
//         );

//         setSubcategories(filteredSubcategories);
//         // Reset serviceid when category changes
//         setFormData((prev) => ({ ...prev, serviceid: "" }));
//       } catch (error) {
//         console.error("Error fetching subcategories:", error);
//       }
//     };

//     if (selectedService) {
//       fetchSubcategories();
//     }
//   }, [selectedService, setFormData]);

//   // Filter items when service is selected or when category changes
//   useEffect(() => {
//     if (formData.serviceid) {
//       // Filter items based on service ID
//       const filteredItems = allItems.filter(
//         (item) => item.serviceid == formData.serviceid
//       );
//       setItems(filteredItems);
//     } else if (selectedService?.id) {
//       // If no service selected but category is selected, show items for that category
//       const filteredItems = allItems.filter(
//         (item) => item.categoryid === selectedService.id
//       );
//       setItems(filteredItems);
//     }
//   }, [formData.serviceid, allItems, selectedService]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleDateTimeChange = (e) => {
//     const { name, value } = e.target;

//     setFormData((prev) => {
//       // Get current date/time parts
//       const currentDate =
//         prev.scheduledtime?.split("T")[0] ||
//         new Date().toISOString().split("T")[0];
//       const currentTime =
//         prev.scheduledtime?.split("T")[1]?.substring(0, 5) || "00:00";

//       // Update the relevant part
//       let newDate = currentDate;
//       let newTime = currentTime;

//       if (name === "date") {
//         newDate = value; // YYYY-MM-DD format from date input
//       } else if (name === "time") {
//         newTime = value; // HH:MM format from time input
//       }

//       // Combine into ISO format
//       return {
//         ...prev,
//         scheduledtime: `${newDate}T${newTime}:00.000Z`, // Full ISO datetime
//       };
//     });
//   };

//   const handlePreview = async () => {
//     // If no item selected, we'll use the default service pricing
//     if (!formData.serviceid) {
//       alert("Please select a service first");
//       return;
//     }

//     try {
//       let previewResponse;

//       if (formData.itemid && formData.quantity) {
//         // If item is selected, get preview for that specific item
//         const selectedItem = items.find((item) => item.id == formData.itemid);
//         if (!selectedItem) {
//           throw new Error("Selected item not found");
//         }

//         previewResponse = await fetch(
//           `${BQ_BASE_URL}/bq/api/room-service-requests/preview/${formData.itemid}/${formData.quantity}`
//         );
//       } else {
//         // If no item selected, get preview for the default service
//         previewResponse = await fetch(
//           `${BQ_BASE_URL}/bq/api/room-service-requests/preview-service/${formData.serviceid}`
//         );
//       }

//       const data = await previewResponse.json();

//       // For default service preview, the API should return base and urgent amounts
//       // If it doesn't, we'll fall back to default values
//       setPreviewData({
//         item_name: data.item_name || selectedService?.name || "Service",
//         base_amount: data.base_amount || 0,
//         urgent_amount: data.urgent_amount || 0,
//         quantity: data.quantity || 1,
//       });

//       setShowPreview(true);
//     } catch (error) {
//       console.error("Error fetching preview:", error);

//       // Fallback to showing just the service name if API fails
//       setPreviewData({
//         item_name: selectedService?.name || "Service",
//         base_amount: 0,
//         urgent_amount: 0,
//         quantity: 1,
//       });
//       setShowPreview(true);
//     }
//   };

//   const onBook = async (formData) => {
//     const apiUrl = `${BQ_BASE_URL}/bq/api/room-service-requests/`;

//     try {
//       if (!bookingId) {
//         throw new Error("Booking ID not found. Please try again.");
//       }

//       // Validate required fields - only service is required now
//       if (!formData.serviceid) {
//         throw new Error("Please select a service");
//       }

//       // Calculate amount based on whether item is selected or not
//       let baseAmount = 0;
//       let urgentAmount = 0;

//       if (formData.itemid && formData.quantity) {
//         const selectedItem = items.find((item) => item.id == formData.itemid);
//         if (!selectedItem) {
//           throw new Error("Selected item not found");
//         }
//         baseAmount = selectedItem.baseprice * formData.quantity;
//         urgentAmount = selectedItem.urgentprice * formData.quantity;
//       }

//       const requestBody = {
//         bookingid: bookingId,
//         categoryid: selectedService?.id,
//         serviceid: formData.serviceid,
//         itemid: formData.itemid || null, // Can be null now
//         quantity: formData.quantity || 1, // Default to 1 if not specified
//         urgencylevel: formData.urgencylevel || "Normal",
//         scheduledtime: formData.scheduledtime,
//         specialinstructions: formData.specialinstructions || "No instructions",
//         status: "Pending",
//         base_amount:
//           formData.urgencylevel === "Urgent" ? urgentAmount : baseAmount,
//       };

//       const response = await fetch(apiUrl, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(requestBody),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(
//           errorData.detail?.[0]?.msg || `HTTP error! status: ${response.status}`
//         );
//       }

//       const data = await response.json();
//       toast.success("Room service booked successfully!");
//       await confirmServiceBooking();
//       onClose();
//       return data;
//     } catch (error) {
//       console.error("Error making booking:", error);
//       toast.error(`Booking failed: ${error.message}`);
//       throw error;
//     }
//   };

//   const closePreview = () => {
//     setShowPreview(false);
//     setPreviewData(null);
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "decimal",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(amount);
//   };

//   return (
//     <>
//       {/* Main Booking Modal */}
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
//         <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
//           <div className="p-6 relative">
//             <div className="flex justify-between items-start mb-6">
//               <div>
//                 <h2 className="text-2xl font-poppins font-bold text-gray-800">
//                   Book {selectedService?.name}
//                 </h2>
//                 <p className="text-gray-500 font-poppins text-sm mt-1">
//                   Fill in the details to schedule your service
//                 </p>
//               </div>
//               <button
//                 className="text-red-500 hover:text-red-700 transition-colors duration-200"
//                 onClick={onClose}
//               >
//                 <IoClose size={32} />
//               </button>
//             </div>

//             <input
//               type="hidden"
//               name="categoryid"
//               value={selectedService?.id}
//             />
//             <input type="hidden" name="status" value="Pending" />

//             <div className="grid grid-cols-2 gap-6">
//               {/* Service and Item in one row */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Select a Service <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   name="serviceid"
//                   value={formData.serviceid}
//                   onChange={handleInputChange}
//                   className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 >
//                   <option value="" disabled>
//                     Select a service
//                   </option>
//                   {subcategories.map((sub) => (
//                     <option key={sub.id} value={sub.id}>
//                       {sub.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Items Dropdown - now optional */}
//               {items.length > 0 && (
//                 <div className="col-span-1">
//                   <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                     Select an Item
//                   </label>
//                   <select
//                     name="itemid"
//                     value={formData.itemid || ""}
//                     onChange={handleInputChange}
//                     className="w-full px-4 font-poppins py-2.5 border border-gray-200 rounded-lg duration-200"
//                   >
//                     <option value="">Select your specific item</option>
//                     {items.map((item) => (
//                       <option key={item.id} value={item.id}>
//                         {item.name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )}

//               {/* Urgency Level and Quantity in one row */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Urgency Level
//                 </label>
//                 <select
//                   name="urgencylevel"
//                   value={formData.urgencylevel || "Normal"}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="Normal">Normal</option>
//                   <option value="Urgent">Urgent</option>
//                 </select>
//               </div>

//               {/* Quantity - only shown if item is selected */}
//               {formData.itemid && (
//                 <div className="col-span-1">
//                   <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                     Quantity
//                   </label>
//                   <input
//                     type="number"
//                     name="quantity"
//                     placeholder="Enter Your Quantity"
//                     value={formData.quantity}
//                     onChange={handleInputChange}
//                     className="w-full px-4 py-2.5 font-poppins border border-gray-200 rounded-lg duration-200"
//                   />
//                 </div>
//               )}

//               {/* Schedule Date and Time in one row */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Schedule Date <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="date"
//                   name="date"
//                   value={formData.scheduledtime?.split("T")[0] || ""}
//                   onChange={handleDateTimeChange}
//                   min={new Date().toISOString().split("T")[0]}
//                   className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Schedule Time */}
//               <div className="col-span-1">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Schedule Time <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="time"
//                   name="time"
//                   value={
//                     formData.scheduledtime?.split("T")[1]?.substring(0, 5) || ""
//                   }
//                   onChange={handleDateTimeChange}
//                   className="w-full font-poppins px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Special Instructions - full width */}
//               <div className="col-span-2">
//                 <label className="block font-poppins text-gray-700 text-md font-medium mb-1">
//                   Special Instructions
//                 </label>
//                 <textarea
//                   name="specialinstructions"
//                   value={formData.specialinstructions}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   placeholder="Any special requests or instructions"
//                 ></textarea>
//               </div>
//             </div>

//             {/* Buttons */}
//             <div className="mt-8 flex justify-end space-x-4">
//               <button
//                 onClick={handlePreview}
//                 className="px-10 py-2.5 font-poppins bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
//               >
//                 Preview
//               </button>
//               <button
//                 onClick={async () => {
//                   setIsBooking(true);
//                   try {
//                     await onBook(formData);
//                   } catch (error) {
//                     // Error is already handled by the toast in onBook
//                   } finally {
//                     setIsBooking(false);
//                   }
//                 }}
//                 disabled={isBooking}
//                 className={`px-6 py-2.5 font-poppins text-white font-medium rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
//                   isBooking ? "opacity-70 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isBooking ? (
//                   <span className="flex items-center justify-center">
//                     <svg
//                       className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                     >
//                       <circle
//                         className="opacity-25"
//                         cx="12"
//                         cy="12"
//                         r="10"
//                         stroke="currentColor"
//                         strokeWidth="4"
//                       ></circle>
//                       <path
//                         className="opacity-75"
//                         fill="currentColor"
//                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                       ></path>
//                     </svg>
//                     Processing...
//                   </span>
//                 ) : (
//                   "Book Now"
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Preview Modal */}
//       {showPreview && (
//         <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
//           <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scaleIn">
//             <div className="relative">
//               {/* Header with gradient background */}
//               <div className="bg-gradient-to-r from-black to-gray-700 p-6">
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <h2 className="text-2xl font-bold text-white">
//                       Order Summary
//                     </h2>
//                     <p className="text-blue-100 text-sm mt-1">
//                       Review your order before confirmation
//                     </p>
//                   </div>
//                   <button
//                     className="text-white hover:text-blue-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10"
//                     onClick={closePreview}
//                   >
//                     <IoClose size={24} />
//                   </button>
//                 </div>
//               </div>

//               {/* Content */}
//               <div className="p-6 space-y-5">
//                 {/* Order Items Card */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <div className="flex items-center space-x-3 mb-3">
//                     <div className="bg-blue-100 p-2 rounded-lg">
//                       <svg
//                         className="w-6 h-6 text-blue-600"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
//                         />
//                       </svg>
//                     </div>
//                     <div>
//                       <h3 className="font-semibold text-gray-800">
//                         {previewData?.item_name}
//                       </h3>
//                       {formData.itemid && (
//                         <p className="text-sm text-gray-500">
//                           Qty: {previewData?.quantity}
//                         </p>
//                       )}
//                     </div>
//                   </div>

//                   <div className="space-y-3 pt-3 border-t border-gray-200">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Urgency:</span>
//                       <span
//                         className={`font-medium ${
//                           formData.urgencylevel === "Urgent"
//                             ? "text-red-500"
//                             : "text-gray-700"
//                         }`}
//                       >
//                         {formData.urgencylevel || "Normal"}
//                       </span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Scheduled for:</span>
//                       <span className="font-medium text-gray-700">
//                         {new Date(formData.scheduledtime).toLocaleString()}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Pricing Breakdown */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <h4 className="font-medium text-lg text-black mb-4">
//                     Price Details
//                   </h4>
//                   <div className="space-y-2">
//                     {formData.itemid ? (
//                       <>
//                         <div className="flex justify-between">
//                           <span className="text-gray-600">Subtotal:</span>
//                           <span className="font-medium">
//                             ₹{formatCurrency(previewData?.base_amount)}
//                           </span>
//                         </div>
//                         {formData.urgencylevel === "Urgent" && (
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">
//                               Urgent Surcharge
//                             </span>
//                             <span className="font-medium text-red-500">
//                               +₹
//                               {formatCurrency(
//                                 previewData?.urgent_amount -
//                                   previewData?.base_amount
//                               )}
//                             </span>
//                           </div>
//                         )}
//                       </>
//                     ) : (
//                       <div className="flex justify-between">
//                         <span className="text-gray-600">Service Fee</span>
//                         <span className="font-medium">
//                           {formData.urgencylevel === "Urgent" ? (
//                             <>₹{formatCurrency(previewData?.urgent_amount)}</>
//                           ) : (
//                             <>₹{formatCurrency(previewData?.base_amount)}</>
//                           )}
//                         </span>
//                       </div>
//                     )}
//                     <div className="pt-2 border-t border-gray-200 flex justify-between">
//                       <span className="font-semibold text-gray-800">Total</span>
//                       <span className="font-bold text-blue-600">
//                         ₹
//                         {formatCurrency(
//                           formData.urgencylevel === "Urgent"
//                             ? previewData?.urgent_amount
//                             : previewData?.base_amount
//                         )}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Special Instructions */}
//                 {formData.specialinstructions && (
//                   <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                     <h4 className="font-medium text-gray-700 mb-2">
//                       Special Instructions
//                     </h4>
//                     <p className="text-gray-700 italic">
//                       "{formData.specialinstructions}"
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer with action buttons */}
//               <div className="px-6 pb-6 pt-2 bg-gray-50 border-t border-gray-100">
//                 <button
//                   onClick={closePreview}
//                   className="w-full py-3 px-4 bg-black rounded-lg font-semibold text-white hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
//                 >
//                   Back to Edit
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer />
//     </>
//   );
// };

// export default BookingModal;
